import { CheckCircle2Icon, ExternalLinkIcon, XCircleIcon } from 'lucide-react-native';
import * as React from 'react';
import { Linking, View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@sixam/ui/card';
import { Text } from '@sixam/ui/text';
import { formatEventPrice } from '@/lib/event-registration';
import {
  fetchAdminEventPayments,
  reviewAdminEventPayment,
  type AdminEventPaymentRow,
} from '@/lib/event-payment-review';

type AdminEventPaymentsPanelProps = Readonly<{
  eventId: string;
  flat?: boolean;
  tenantSlug: string;
}>;

export function AdminEventPaymentsPanel({
  eventId,
  flat = false,
  tenantSlug,
}: AdminEventPaymentsPanelProps) {
  const [rows, setRows] = React.useState<AdminEventPaymentRow[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [reviewingPaymentId, setReviewingPaymentId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;

    async function load() {
      setIsLoading(true);
      try {
        const nextRows = await fetchAdminEventPayments(tenantSlug, eventId);
        if (active) {
          setRows(nextRows);
          setError(null);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load payments.');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [eventId, tenantSlug]);

  async function review(paymentId: string, decision: 'approve' | 'reject') {
    setReviewingPaymentId(paymentId);
    setError(null);
    try {
      const nextRows = await reviewAdminEventPayment(tenantSlug, eventId, paymentId, decision);
      setRows(nextRows);
    } catch (reviewError) {
      setError(reviewError instanceof Error ? reviewError.message : 'Could not review payment.');
    } finally {
      setReviewingPaymentId(null);
    }
  }

  return (
    <AdminEventPaymentsSummary
      error={error}
      flat={flat}
      isLoading={isLoading}
      onApprove={(paymentId) => void review(paymentId, 'approve')}
      onReject={(paymentId) => void review(paymentId, 'reject')}
      reviewingPaymentId={reviewingPaymentId}
      rows={rows}
    />
  );
}

export function AdminEventPaymentsSummary({
  error,
  flat = false,
  isLoading,
  onApprove,
  onReject,
  reviewingPaymentId,
  rows,
}: Readonly<{
  error?: string | null;
  flat?: boolean;
  isLoading?: boolean;
  onApprove?: (paymentId: string) => void;
  onReject?: (paymentId: string) => void;
  reviewingPaymentId?: string | null;
  rows: AdminEventPaymentRow[];
}>) {
  const paidCount = rows.filter((row) => row.registration.status === 'confirmed').length;
  const reviewCount = rows.filter((row) => row.latestPayment?.status === 'manual_review').length;

  const content = (
    <View className="gap-4">
      <Text variant="muted">
        {rows.length} registrations · {paidCount} paid · {reviewCount} need review
      </Text>

      {error ? <Text className="text-sm font-medium text-destructive">{error}</Text> : null}
      {isLoading ? <Text variant="mono">Loading payments...</Text> : null}

      {!isLoading && rows.length === 0 ? (
        <View className="rounded-md border border-border p-4">
          <Text variant="large">No registrations yet</Text>
          <Text variant="muted">Paid registrations will appear here after members register.</Text>
        </View>
      ) : null}

      {rows.map((row) => (
        <PaymentRow
          key={row.registration.id}
          onApprove={onApprove}
          onReject={onReject}
          reviewingPaymentId={reviewingPaymentId}
          row={row}
        />
      ))}
    </View>
  );

  if (flat) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payments</CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

function PaymentRow({
  onApprove,
  onReject,
  reviewingPaymentId,
  row,
}: Readonly<{
  onApprove?: (paymentId: string) => void;
  onReject?: (paymentId: string) => void;
  reviewingPaymentId?: string | null;
  row: AdminEventPaymentRow;
}>) {
  const payment = row.latestPayment;
  const needsReview = payment?.status === 'manual_review';
  const isReviewing = payment ? reviewingPaymentId === payment.id : false;
  const memberLabel = row.member.fullName ?? row.member.email ?? row.member.id;

  return (
    <View className="gap-3 border-b border-border py-4">
      <View className="gap-1 sm:flex-row sm:items-start sm:justify-between">
        <View className="min-w-0 flex-1 gap-1">
          <Text variant="large" numberOfLines={1}>
            {memberLabel}
          </Text>
          <Text variant="muted" numberOfLines={1}>
            {row.member.email ?? row.member.id}
          </Text>
        </View>
        <View className="gap-1 sm:items-end">
          <Text variant="label" className="text-muted-foreground">
            {row.registration.status.replace('_', ' ')}
          </Text>
          <Text variant="mono">
            {payment ? formatEventPrice(payment.amount_thb) : 'No payment'}
          </Text>
        </View>
      </View>

      {payment ? (
        <View className="gap-1">
          <Text variant="mono">
            {payment.method.replace('_', ' ')} · {payment.status.replace('_', ' ')}
          </Text>
          {payment.validation_error ? (
            <Text className="text-sm text-destructive">{payment.validation_error}</Text>
          ) : null}
        </View>
      ) : null}

      {row.receiptUrl ? (
        <Button variant="outline" size="sm" onPress={() => void Linking.openURL(row.receiptUrl!)}>
          <ExternalLinkIcon color="white" size={16} />
          <Text>Open receipt</Text>
        </Button>
      ) : null}

      {needsReview && payment ? (
        <View className="gap-2 sm:flex-row">
          <Button
            className="sm:flex-1"
            disabled={isReviewing}
            onPress={() => onApprove?.(payment.id)}
            size="sm"
          >
            <CheckCircle2Icon color="white" size={16} />
            <Text>{isReviewing ? 'Reviewing...' : 'Approve'}</Text>
          </Button>
          <Button
            className="sm:flex-1"
            disabled={isReviewing}
            onPress={() => onReject?.(payment.id)}
            size="sm"
            variant="destructive"
          >
            <XCircleIcon color="white" size={16} />
            <Text>Reject</Text>
          </Button>
        </View>
      ) : null}
    </View>
  );
}
